/**
 * ADR-0211 §6.3 — the agent-card REDIRECT install for script-tag sites.
 *
 * `/.well-known/agent-card.json → 301 → https://edge.sill.so/v1/agent-card/<key>.json`
 * is available on almost every host and never goes stale when skills
 * change, so it is offered BEFORE the file upload. One snippet per host;
 * `generic` is the honest fallback ("look for Redirects in your host's
 * settings").
 */

export type RedirectHost =
  | 'netlify'
  | 'vercel'
  | 'nginx'
  | 'apache'
  | 'webflow'
  | 'squarespace'
  | 'wix'
  | 'generic';

export const REDIRECT_HOSTS: ReadonlyArray<{ id: RedirectHost; label: string }> = [
  { id: 'netlify', label: 'Netlify' },
  { id: 'vercel', label: 'Vercel' },
  { id: 'nginx', label: 'nginx' },
  { id: 'apache', label: 'Apache' },
  { id: 'webflow', label: 'Webflow' },
  { id: 'squarespace', label: 'Squarespace' },
  { id: 'wix', label: 'Wix' },
  { id: 'generic', label: 'Other' },
];

export interface RedirectInstructions {
  /** `code` → show in a code block with Copy; `steps` → a numbered list. */
  readonly kind: 'code' | 'steps';
  /** Where the code goes (file name) or a one-line lead for steps. */
  readonly where: string;
  readonly lines: readonly string[];
}

const PATH = '/.well-known/agent-card.json';

export function redirectInstructions(host: RedirectHost, card_url: string): RedirectInstructions {
  switch (host) {
    case 'netlify':
      return { kind: 'code', where: '_redirects (in your publish directory)', lines: [`${PATH}  ${card_url}  301`] };
    case 'vercel':
      return {
        kind: 'code',
        where: 'vercel.json',
        lines: [
          '{',
          '  "redirects": [',
          `    { "source": "${PATH}", "destination": "${card_url}", "permanent": true }`,
          '  ]',
          '}',
        ],
      };
    case 'nginx':
      return { kind: 'code', where: 'inside your server { } block', lines: [`location = ${PATH} { return 301 ${card_url}; }`] };
    case 'apache':
      return { kind: 'code', where: '.htaccess (site root)', lines: [`Redirect 301 ${PATH} ${card_url}`] };
    case 'webflow':
      return {
        kind: 'steps',
        where: 'Webflow → Site settings → Publishing → 301 redirects',
        lines: [`Old path: ${PATH}`, `Redirect to: ${card_url}`, 'Add redirect, then publish the site.'],
      };
    case 'squarespace':
      return {
        kind: 'steps',
        where: 'Squarespace → Settings → Developer tools → URL mappings',
        lines: [`Add the line: ${PATH} -> ${card_url} 301`, 'Save. Squarespace applies it immediately.'],
      };
    case 'wix':
      return {
        kind: 'steps',
        where: 'Wix → Marketing & SEO → SEO → URL redirect manager',
        lines: [`Old URL: ${PATH}`, `New URL: ${card_url}`, 'Save. Wix applies it immediately.'],
      };
    case 'generic':
      return {
        kind: 'steps',
        where: 'In your host’s settings, open Redirects.',
        lines: [`From: ${PATH}`, `To: ${card_url}`, 'Type: 301. No redirects? Use Other ways to serve it.'],
      };
  }
}

/** Pick the preselected host from the detector's hints. */
export function redirectHostFromHints(hints: { hosting_hint?: string | undefined; vendor_hint?: string | undefined }): RedirectHost {
  if (hints.hosting_hint === 'netlify' || hints.hosting_hint === 'vercel') return hints.hosting_hint;
  if (hints.vendor_hint === 'webflow' || hints.vendor_hint === 'squarespace' || hints.vendor_hint === 'wix') return hints.vendor_hint;
  return 'generic';
}
